// packages/shared/src/client/components/FeedbackWidget.tsx
// Widget de feedback flottant (bug / avis / suggestion) partagé par les apps
// Academy & Prompts. Réservé aux utilisateurs connectés. Envoie POST /feedback
// avec un token Cap (proof-of-work) obligatoire. Stylé avec le design system
// NeuroForge (tokens --accent / --border / --bg-elevated / --muted, AppIcon).
import '@cap.js/widget'
import {
  AppIcon,
  cn,
  IconBug,
  IconBulb,
  IconCircleCheck,
  IconMessageCircle,
  IconSend,
  IconStar,
  IconX,
} from '@neuroforge/ui'
import { useEffect, useRef, useState, type FormEvent } from 'react'

import { type FeedbackSource, FeedbackType } from '@neuroforge/shared'

import { api } from '../api-client'
import { useAuthStore } from '../stores/auth.store'

// Endpoint Cap (proof-of-work) — fallback sur le proxy Vite `/api/v1` si
// VITE_API_URL n'est pas défini (évite un endpoint `undefined/cap/`).
const CAP_API = `${import.meta.env.VITE_API_URL ?? '/api/v1'}/cap/`

interface TypeOption {
  value: FeedbackType
  label: string
  icon: typeof IconBug
}

const TYPE_OPTIONS: TypeOption[] = [
  { value: FeedbackType.BUG, label: 'Bug', icon: IconBug },
  { value: FeedbackType.AVIS, label: 'Avis', icon: IconStar },
  { value: FeedbackType.SUGGESTION, label: 'Suggestion', icon: IconBulb },
]

export interface FeedbackWidgetProps {
  /** App émettrice du feedback (renseigne la colonne `source`). */
  source: FeedbackSource
}

export function FeedbackWidget({ source }: FeedbackWidgetProps) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)

  const [open, setOpen] = useState(false)
  const [type, setType] = useState<FeedbackType>(FeedbackType.BUG)
  const [message, setMessage] = useState('')
  const [capToken, setCapToken] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const capRef = useRef<HTMLElement | null>(null)

  // Écoute l'événement "solve" du widget Cap (web component).
  useEffect(() => {
    const el = capRef.current
    if (!el) return
    const onSolve = (e: Event) => {
      const detail = (e as CustomEvent).detail as { token: string }
      setCapToken(detail.token)
    }
    el.addEventListener('solve', onSolve)
    return () => el.removeEventListener('solve', onSolve)
  }, [open])

  function resetForm() {
    setType(FeedbackType.BUG)
    setMessage('')
    setCapToken(null)
    setError(null)
    setIsSuccess(false)
  }

  function close() {
    setOpen(false)
    setTimeout(resetForm, 200)
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)

    if (message.trim().length < 5) {
      setError('Merci de détailler un peu votre retour (5 caractères minimum).')
      return
    }
    if (!capToken) {
      setError('Merci de valider le captcha.')
      return
    }

    setIsSubmitting(true)
    try {
      await api.post('/feedback', {
        type,
        message: message.trim(),
        source,
        pageUrl: window.location.href,
        capToken,
      })
      setIsSuccess(true)
    } catch {
      setError("Impossible d'envoyer votre retour pour le moment. Réessayez plus tard.")
      setCapToken(null)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <>
      {/* Bouton flottant */}
      <button
        type="button"
        data-testid="feedback-fab"
        onClick={() => setOpen((o) => !o)}
        aria-label="Donner mon avis"
        className="fixed bottom-6 right-6 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-indigo-500 to-violet-600 text-white shadow-lg shadow-violet-500/30 transition-transform hover:-translate-y-0.5"
      >
        <AppIcon icon={open ? IconX : IconMessageCircle} size={24} aria-hidden />
      </button>

      {/* Panneau */}
      {open && (
        <div
          data-testid="feedback-panel"
          className="glass animate-slide-down fixed bottom-24 right-6 z-50 w-[calc(100vw-3rem)] max-w-sm rounded-2xl shadow-2xl"
        >
          <div className="border-(--border) flex items-center justify-between border-b px-5 py-4">
            <div>
              <p className="text-sm font-bold">Votre avis compte</p>
              <p className="text-(--muted) text-xs">Aidez-nous à améliorer NeuroForge</p>
            </div>
            <button
              type="button"
              onClick={close}
              aria-label="Fermer"
              className="hover:bg-(--bg-elevated) rounded-full p-1 transition"
            >
              <AppIcon icon={IconX} size={20} aria-hidden />
            </button>
          </div>

          <div className="p-5">
            {!isAuthenticated ? (
              <div className="border-(--border) bg-(--bg-elevated) text-(--muted) rounded-xl border p-4 text-sm">
                Vous devez être connecté pour nous partager votre avis.
              </div>
            ) : isSuccess ? (
              <div className="flex flex-col items-center gap-3 py-4 text-center">
                <AppIcon
                  icon={IconCircleCheck}
                  size={28}
                  className="text-emerald-500"
                  aria-hidden
                />
                <p className="font-bold">Merci pour votre retour !</p>
                <p className="text-(--muted) text-sm">Nous l'avons bien reçu.</p>
                <button
                  type="button"
                  onClick={resetForm}
                  className="text-(--accent) mt-1 text-sm font-bold underline"
                >
                  Envoyer un autre retour
                </button>
              </div>
            ) : (
              <form onSubmit={onSubmit} data-testid="feedback-form" className="space-y-4">
                <div className="grid grid-cols-3 gap-2">
                  {TYPE_OPTIONS.map((opt) => {
                    const active = type === opt.value
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setType(opt.value)}
                        className={cn(
                          'flex flex-col items-center gap-1 rounded-xl border px-2 py-3 text-xs font-semibold transition',
                          active
                            ? 'border-(--accent) text-(--accent) bg-(--bg-elevated)'
                            : 'border-(--border) text-(--muted) hover:border-(--accent)',
                        )}
                      >
                        <AppIcon icon={opt.icon} size={20} aria-hidden />
                        {opt.label}
                      </button>
                    )
                  })}
                </div>

                <textarea
                  rows={4}
                  data-testid="feedback-textarea"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  maxLength={2000}
                  placeholder="Décrivez votre retour..."
                  className="border-(--border) bg-(--bg-elevated) focus:border-(--accent) w-full resize-none rounded-xl border px-3 py-2 text-sm outline-none"
                  disabled={isSubmitting}
                />

                {/* Cap — proof-of-work captcha */}
                <div className="flex justify-center">
                  {/* @ts-expect-error cap-widget is a web component */}
                  <cap-widget
                    ref={capRef}
                    data-cap-api-endpoint={CAP_API}
                    data-cap-i18n-initial-state="Vérifier que je suis humain"
                    data-cap-i18n-verifying-label="Vérification…"
                    data-cap-i18n-solved-label="Vérifié ✓"
                    data-cap-i18n-error-label="Erreur"
                  />
                </div>

                {error && (
                  <p className="flex items-center gap-1.5 text-xs font-medium text-red-500">
                    {error}
                  </p>
                )}

                <button
                  type="submit"
                  data-testid="feedback-submit"
                  disabled={isSubmitting || !capToken}
                  className="bg-(--accent) flex w-full items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-bold text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <AppIcon icon={IconSend} size={16} aria-hidden />
                  {isSubmitting ? 'Envoi...' : 'Envoyer'}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  )
}
